import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=Minimal%20sneaker%20silhouette%20on%20clean%20cream%20background%2C%20soft%20lighting%2C%20simple%20product%20photography%2C%20neutral%20tones&width=100&height=100&seq=dd-cat-shoes&orientation=squarish',
  'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=Soft%20folded%20hoodie%20fabric%20texture%20on%20cream%20background%2C%20minimal%20style%2C%20warm%20lighting%2C%20clean%20product%20photography&width=100&height=100&seq=dd-cat-hoodies&orientation=squarish',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Folded%20cotton%20t-shirt%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20clean%20editorial%20style%2C%20neutral%20warm%20tones&width=100&height=100&seq=dd-cat-tshirts&orientation=squarish',
  'Jackets': 'https://readdy.ai/api/search-image?query=Elegant%20jacket%20fabric%20detail%20on%20minimal%20cream%20background%2C%20soft%20lighting%2C%20clean%20product%20photography&width=100&height=100&seq=dd-cat-jackets&orientation=squarish',
  'Pants / Shorts': 'https://readdy.ai/api/search-image?query=Folded%20linen%20fabric%20texture%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=100&height=100&seq=dd-cat-pants&orientation=squarish',
  'Headwear': 'https://readdy.ai/api/search-image?query=Minimal%20baseball%20cap%20on%20clean%20cream%20background%2C%20soft%20lighting%2C%20simple%20product%20photography&width=100&height=100&seq=dd-cat-headwear&orientation=squarish',
  'Sets': 'https://readdy.ai/api/search-image?query=Coordinated%20clothing%20set%20folded%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=100&height=100&seq=dd-cat-sets&orientation=squarish',
  'Underwear / Underpants': 'https://readdy.ai/api/search-image?query=Soft%20folded%20cotton%20fabric%20on%20minimal%20cream%20background%2C%20soft%20lighting%2C%20clean%20product%20photography&width=100&height=100&seq=dd-cat-underwear&orientation=squarish',
  'Jersey': 'https://readdy.ai/api/search-image?query=Sports%20jersey%20fabric%20texture%20on%20minimal%20cream%20background%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=100&height=100&seq=dd-cat-jersey&orientation=squarish',
  'Accessories': 'https://readdy.ai/api/search-image?query=Elegant%20minimal%20watch%20on%20cream%20surface%2C%20soft%20lighting%2C%20clean%20product%20photography%2C%20neutral%20tones&width=100&height=100&seq=dd-cat-accessories&orientation=squarish',
  'Bags': 'https://readdy.ai/api/search-image?query=Premium%20leather%20bag%20detail%20on%20minimal%20cream%20background%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=100&height=100&seq=dd-cat-bags&orientation=squarish',
  'Belts': 'https://readdy.ai/api/search-image?query=Elegant%20leather%20belt%20detail%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20clean%20product%20photography&width=100&height=100&seq=dd-cat-belts&orientation=squarish',
  'Perfumes': 'https://readdy.ai/api/search-image?query=Elegant%20perfume%20bottle%20on%20minimal%20cream%20background%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=100&height=100&seq=dd-cat-perfumes&orientation=squarish',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Various%20small%20fashion%20accessories%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20clean%20product%20photography&width=100&height=100&seq=dd-cat-other&orientation=squarish',
};

export default function DropdownSection() {
  const [selected, setSelected] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelected(cat.name);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener');
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
          Browse Sugargoo Spreadsheet Categories
        </h2>
        <p className="text-foreground-600 mb-8 max-w-xl mx-auto text-sm md:text-base">
          Select a category below to explore curated product collections on our main website. Each category contains dozens of hand-picked items.
        </p>

        <div ref={dropdownRef} className="relative w-full max-w-md mx-auto">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer text-left"
          >
            <span className={`text-sm ${selected ? 'text-foreground-900 font-medium' : 'text-foreground-400'}`}>
              {selected || 'Select a product category...'}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 text-lg transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isOpen && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200/70 rounded-xl py-2 max-h-80 overflow-y-auto z-40">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleSelect(cat)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer text-left"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                    {categoryImages[cat.name] ? (
                      <img
                        src={categoryImages[cat.name]}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-foreground-800 flex-1">{cat.name}</span>
                  <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}